import { useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useBulkRequestConsumersPlanList,
  usePremises,
  usePlan,
  useApplyPlanAddressChange,
} from "@features/cx/reconnect/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { toast } from "@shared/ui/use-toast";
import { FIELD_CLASSES, MODAL_PAGE_SIZE } from "@shared/utils/constants";
import { useState, useMemo } from "react";
import { ClipboardList, Info, Users, MapPin, FileText } from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";

import { useForm } from "react-hook-form";
import { PremisePlanFilter } from "./PremisePlanFilter";

interface PremisePlanRow {
  id: string;
  account: string;
  consumer: string;
  lastPremise: string;
  lastPlan: string;
  servicePremise: string;
  servicePlan: string;
  status: string;
}

interface PremisePlanFilters {
  page: number;
  limit: number;
  search?: string;
  category?: string;
  subCategory?: string;
  premise?: string;
  plan?: string;
}

const PremisePlanTab = () => {
  const { id: batchId } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<PremisePlanFilters>({
    page: 1,
    limit: MODAL_PAGE_SIZE,
    search: "",
    category: "",
    subCategory: "",
    premise: "",
    plan: "",
  });

  const form = useForm({
    defaultValues: {
      premise: "",
      plan: "",
      category: "",
      sub_category: "",
    },
  });

  // Data Fetching
  const { data: consumersPlanResponse, isLoading } =
    useBulkRequestConsumersPlanList({
      batchId: batchId || "",
      remoteUtilityId: Number(remoteUtilityId),
      page: filters.page,
      limit: filters.limit,
      searchData: filters.search,
      category: filters.category,
      sub_category: filters.subCategory,
      premise: filters.premise,
      plan: filters.plan,
    });

  const { data: premisesData } = usePremises(Number(remoteUtilityId));
  const { data: plansData } = usePlan(remoteUtilityId);

  const { mutateAsync: applyAssignment, isPending: isApplying } =
    useApplyPlanAddressChange(batchId || "");

  const handleApplyAssignment = async (values: any) => {
    if (selectedIds.length === 0) {
      toast({
        title: "Warning",
        description: "Please select at least one consumer.",
        variant: "destructive",
      });
      return;
    }

    const payload: any = {
      batch_id: Number(batchId),
      request_ids: selectedIds.map(Number),
    };

    if (values.servicePlan) {
      payload.current_plan_id = values.servicePlan;
    }

    if (values.servicePremise) {
      const selectedPremise = premisesData?.find(
        (p: any) => p.code === values.servicePremise,
      );
      payload.current_address = {
        REGION: selectedPremise?.hierarchyCodes?.region || "",
        COUNTRY: selectedPremise?.hierarchyCodes?.country || "",
        STATE: selectedPremise?.hierarchyCodes?.state || "",
        CITY: selectedPremise?.hierarchyCodes?.county || "",
        ZONE: selectedPremise?.hierarchyCodes?.zone || "",
        DIVISION: selectedPremise?.hierarchyCodes?.division || "",
        AREA: selectedPremise?.hierarchyCodes?.area || "",
        SUBAREA: selectedPremise?.hierarchyCodes?.subArea || "",
        PREMISE: selectedPremise?.hierarchyCodes?.premise || "",
      };
    }

    try {
      await applyAssignment(payload);
      setSelectedIds([]);
      assignForm.reset();
    } catch (error) {
      // Error handled by hook
    }
  };

  const consumersPlanData = useMemo(() => {
    return (
      consumersPlanResponse?.results?.map((item: any) => ({
        id: item.id.toString(),
        account: item.consumerNo || "N/A",
        consumer: `${item.firstName || "N/A"} ${item.lastName || "N/A"}`.trim(),
        lastPremise: item.previousPremiseData?.premise || "N/A",
        lastPlan: item.previousPlanName || "N/A",
        servicePremise: item.currentPremiseData.premise || "Not Assigned",
        servicePlan: item.currentPlanName || "Not Assigned",
        status: item.assignedStatus?.toLowerCase() || "Not Assigned",
      })) || []
    );
  }, [consumersPlanResponse]);

  const summary = consumersPlanResponse?.summary;

  // Form Configuration
  const assignFields: FormField[] = useMemo(
    () => [
      {
        name: "servicePremise",
        label: "Service Premise",
        type: "select",
        className: "col-span-6",
        options:
          premisesData?.map((p: any) => ({
            label: p.name,
            value: p.code,
          })) || [],
      },
      {
        name: "servicePlan",
        label: "Service Plan",
        type: "select",
        className: "col-span-6",
        options:
          plansData?.map((p: any) => ({
            label: p.planName,
            value: p.id,
          })) || [],
      },
    ],
    [premisesData, plansData],
  );

  const assignForm = FormService.createForm({
    fields: assignFields,
    defaultValues: { servicePremise: "", servicePlan: "" },
  });

  const columns: TableColumn<PremisePlanRow>[] = [
    {
      header: "Account Number",
      key: "account",
    },
    {
      header: "Consumer Name",
      key: "consumer",
      render: (val) => (
        <span className="font-medium text-neutral-700">{val}</span>
      ),
    },
    {
      header: "Last Premise",
      key: "lastPremise",
    },
    {
      header: "Last Plan",
      key: "lastPlan",
    },
    {
      header: "Service Premise",
      key: "servicePremise",
      render: (val) => (
        <span
          className={
            val === "Not Assigned"
              ? "text-neutral-400 italic"
              : "font-medium text-neutral-700"
          }
        >
          {val}
        </span>
      ),
    },
    {
      header: "Service Plan",
      key: "servicePlan",
      render: (val) => (
        <span
          className={
            val === "Not Assigned"
              ? "text-neutral-400 italic"
              : "font-medium text-neutral-700"
          }
        >
          {val}
        </span>
      ),
    },
    {
      header: "Status",
      key: "status",
      type: "badge",
      badgeConfig: {
        getVariant: (val) => (val === "updated" ? "success" : "warning"),
        getClassName: (val) =>
          val === "updated"
            ? "bg-green-50 text-green-700 border-green-200"
            : "bg-amber-50 text-amber-700 border-amber-200",
      },
    },
  ];

  const handleApplyFilters = (data: any) => {
    setFilters((prev) => ({
      ...prev,
      category: data.category,
      subCategory: data.sub_category,
      premise: data.premise,
      plan: data.plan,
      page: 1,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      page: 1,
      limit: MODAL_PAGE_SIZE,
      search: "",
      category: "",
      subCategory: "",
      premise: "",
      plan: "",
    };
    setFilters(defaultFilters);
    setPage(1);
    form.reset({
      premise: "",
      plan: "",
      category: "",
      sub_category: "",
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({ ...prev, search: searchTerm, page: 1 }));
    setPage(1);
  };

  const totalItems = consumersPlanResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / MODAL_PAGE_SIZE);

  const pagination = {
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: MODAL_PAGE_SIZE,
    startItem: (page - 1) * MODAL_PAGE_SIZE + 1,
    endItem: Math.min(page * MODAL_PAGE_SIZE, totalItems),
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Assignment Control Section */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <ClipboardList className="w-5 h-5 text-blue-600" />
            </div>
            <PageHeader title="Assign Premise & Plan" subtitle="" />
          </div>
          <div className="flex items-center gap-2 bg-blue-50/50 px-4 py-2 rounded-full border border-blue-50">
            <Info size={14} className="text-blue-500" />
            <p className="text-[10px] text-blue-700 font-bold uppercase tracking-tight">
              {selectedIds.length} Consumers Selected
            </p>
          </div>
        </div>

        <DynamicForm
          fields={assignFields}
          form={assignForm}
          gridColumns={12}
          config={{
            layout: "vertical",
            spacing: "comfortable",
            classes: FIELD_CLASSES,
          }}
          renderCustomFooter={() => (
            <div className="flex items-end mb-[5px] justify-end mt-3">
              <Button
                size="lg"
                disabled={selectedIds.length === 0 || isApplying}
                isLoading={isApplying}
                className="bg-blue-600 hover:bg-blue-700 px-8 font-bold shadow-md shadow-blue-100 h-10"
                onClick={assignForm.handleSubmit(handleApplyAssignment)}
              >
                Apply Assignment
              </Button>
            </div>
          )}
        />
      </div>

      {/* Header Section */}
      {/* KPI Section Matching Design */}
      <div className="grid grid-cols-3 gap-4">
        <QuickStatsCard
          title="Total Consumers"
          count={summary?.totalConsumers || 0}
          icon={
            <div className="p-2 bg-blue-50 rounded-full">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
          }
        />
        <QuickStatsCard
          title="Consumers with Premise Updated"
          count={summary?.consumersWithNewPremise || 0}
          icon={
            <div className="p-2 bg-green-50 rounded-full">
              <MapPin className="w-5 h-5 text-green-500" />
            </div>
          }
        />
        <QuickStatsCard
          title="Consumers with Plan Updated"
          count={summary?.consumersWithNewPlan || 0}
          icon={
            <div className="p-2 bg-green-50 rounded-full">
              <FileText className="w-5 h-5 text-green-500" />
            </div>
          }
        />
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden shadow-sm">
        <div className="p-2">
          <GenericTable<PremisePlanRow>
            data={consumersPlanData}
            loading={isLoading}
            columns={columns}
            rowKey="id"
            selectable={true}
            selectedRows={selectedIds}
            onSelectionChange={(ids) => setSelectedIds(ids)}
            onSearch={handleSearch}
            pagination={pagination}
            onPageChange={handlePageChange}
            searchPlaceholder="Search by account or name..."
            filterPanel={
              <PremisePlanFilter
                form={form}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
              />
            }
            getRowClassName={() =>
              "text-xs h-14 border-b last:border-0 hover:bg-indigo-50/20 transition-colors"
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PremisePlanTab;
